import { derive, type GameConfig, type GameState } from '../game/GameState.ts';
import { flapApexRise, predict, predictWithFlaps } from '../game/Physics.ts';
import { doFlap, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

/**
 * What the bot saw and why it decided what it decided.
 *
 * Drawn live by `Renderer.drawBotOverlay` and copied into every
 * BOT_FLAP_REQUESTED event, so a crash 40 s into a session can be explained
 * afterwards from the log alone.
 */
export interface BotDecision {
  flap: boolean;
  /** Vertical position the bot is steering towards, px. */
  targetY: number;
  /** predictedY - targetY. Positive means "predicted to be too low". */
  errorY: number;
  /** Seconds until the next pipe's leading edge reaches the bird. */
  timeToPipe: number;
  reason: string;

  // --------------------------------------------------------------- context --
  tick: number;
  /** Wall clock, ms. */
  timestamp: number;
  /** How far ahead the bot extrapolated, ms. 0 when compensation is off. */
  horizonMs: number;
  predictedY: number;
  predictedVy: number;
  birdY: number;
  birdVelocityY: number;
  gapCenterY: number;
  distanceToNextPipe: number;
  /** Impulses already requested but not yet observed as executed flaps. */
  inFlight: number;
  /** True if a wanted flap was blocked by the bot's own refractory period. */
  suppressed: boolean;
  /**
   * Sampled ballistic forecast for the canvas overlay: where the bird goes over
   * the next second if nothing further is requested. Includes in-flight
   * impulses. Empty when `collectPath` is off (headless trial runs).
   */
  predictedPath: { t: number; y: number }[];
}

export interface BotParams {
  /**
   * Where to aim relative to the gap centre, as a fraction of the flap's apex
   * rise (positive = below centre).
   *
   * A flap raises the bird by `flapApexRise` px, so the steady state is a
   * sawtooth spanning roughly [target - rise, target]. Aiming half a rise below
   * the gap centre puts that sawtooth in the middle of the gap.
   *
   * Expressed as a fraction rather than in pixels on purpose: the apex rise
   * changes by a factor of three across the difficulty presets, and a fixed
   * pixel offset that centres the sawtooth in one of them puts the bird into
   * the pipe lip in another. This value survives dragging the gravity slider.
   */
  aimFraction: number;
  /**
   * Dead band around the target, px. Larger = fewer, coarser flaps -- and in
   * stimulated mode every flap is a real stimulation delivered to a person, so
   * this doubles as the "be frugal with the participant" knob.
   */
  deadband: number;
  /**
   * Actuator latency the bot compensates for, ms.
   *
   * 0 for keyboard/direct control. For simulated or human-mediated control this
   * should be the measured median from `CalibrationSession`, not a guess.
   */
  latencyMs: number;
  /** Master switch for latency compensation. Off = the A/B control condition. */
  latencyCompensation: boolean;
  /** Fold impulses that are already on their way into the prediction. */
  accountForInFlight: boolean;
  /**
   * How long past its expected arrival to keep believing in a request, ms.
   *
   * This is the single most performance-critical parameter in mediated mode.
   * While the bot believes an impulse is coming it stays quiet, so every
   * millisecond of misplaced faith in a response that will never arrive is a
   * millisecond of free fall.
   *
   * Too short and the bot double-stimulates a merely slow participant. Too long
   * and a 5% miss rate is fatal: the bird dies within 20 s at a 300 ms grace and
   * survives indefinitely at 60 ms. Set it from calibration -- roughly
   * (p95 - median) of the measured response latency, i.e. cover the slow tail
   * and write off anything past it.
   */
  inFlightGraceMs: number;
  /** Minimum interval between successive flap intents, ms. */
  minIntentIntervalMs: number;
  /** Refuse to flap if the resulting arc would come this close to the ceiling. */
  ceilingMargin: number;
  /** Flap regardless if predicted to come this close to the floor, px. */
  floorMargin: number;
  /**
   * Beyond this distance to the next pipe (px), blend the aim point towards the
   * pipe *after* it, so a large vertical step is already being absorbed when it
   * arrives instead of being met at the last moment.
   */
  lookaheadDistance: number;
  /**
   * How strongly to blend towards the upcoming gap, 0..1.
   *
   * This matters far more at low gravity than at high. The bird can only
   * descend at its steady sink rate, so a big drop between two gaps has to be
   * started early; with a weak blend the bot notices the drop only once the
   * pipe is close and then physically cannot get down in time. Raising this is
   * what makes the low-stimulation presets flyable at all.
   */
  lookaheadWeight: number;
  /** Sample a forecast path for the canvas overlay. Off in headless trials. */
  collectPath: boolean;
}

/**
 * Defaults from a grid search over 10 seeds x 6 actuator conditions
 * (direct, fixed 50/100/150/200 ms, and a noisy simulated human), 60 s each --
 * see `npm run bench`. The surface is flat around this point; nothing here is
 * finely balanced, and every value is meant to be edited from the dashboard.
 *
 * `inFlightGraceMs` is the exception: it is genuinely sharp. Dropping it to 0
 * costs a third of total survival time, and raising it to 60 makes a 5% miss
 * rate fatal.
 */
export const DEFAULT_BOT_PARAMS: BotParams = {
  aimFraction: 0.45,
  deadband: 4,
  latencyMs: 0,
  latencyCompensation: true,
  accountForInFlight: true,
  inFlightGraceMs: 10,
  minIntentIntervalMs: 40,
  ceilingMargin: 24,
  floorMargin: 90,
  lookaheadDistance: 170,
  lookaheadWeight: 1,
  collectPath: true,
};

/** How far ahead the overlay forecast runs, seconds. */
const PATH_HORIZON_SEC = 1.0;
const PATH_SAMPLES = 40;

/**
 * Rule-based autonomous controller.
 *
 * The whole policy is four lines of arithmetic:
 *
 *   1. Predict where the bird will be when an impulse issued *now* would
 *      actually land -- `latencyMs` from now, counting impulses already in
 *      flight.
 *   2. Aim at the centre of the next gap, biased down by `aimOffset` and
 *      blended towards the pipe after it when there is time.
 *   3. Flap if that prediction is below the aim point.
 *   4. Never flap into the ceiling; always flap away from the floor.
 *
 * Step 1 is the entire point of the project. With `latencyMs = 0` this is an
 * ordinary reactive Flappy bot. With `latencyMs = 120` it is a bot flying a
 * bird through a human arm, and it has to release the stimulation request
 * before it can see the reason for it.
 *
 * A short-horizon rollout planner was tried here as a safety veto over this
 * rule. It changed the decision on 0.28% of ticks in the worst condition, never
 * in the others, for ~50x the CPU. It was removed; this rule is the controller.
 *
 * Used two ways:
 *   1. As a `Controller` on its own -- "Direct Bot" mode, intent == execution.
 *   2. As the *intent source* inside `StimulationController`, where a decision
 *      becomes a stimulation request and something else entirely decides
 *      whether a flap ever happens.
 */
export class BotController implements Controller {
  readonly id = 'bot';
  readonly label = 'Bot (direct)';

  params: BotParams;
  lastDecision: BotDecision | null = null;

  /** Wall-clock times (ms) at which requested impulses are expected to land. */
  private inFlight: { requestId: string; etaMs: number; issuedMs: number }[] = [];
  private lastIntentMs = -Infinity;

  constructor(
    private readonly config: GameConfig,
    params: Partial<BotParams> = {},
  ) {
    this.params = { ...DEFAULT_BOT_PARAMS, ...params };
  }

  /** Direct mode: the decision *is* the flap, with no actuator in between. */
  update(state: GameState): ControllerOutput {
    const decision = this.decide(state);
    if (decision.flap) {
      this.noteIntent('direct', state.timestamp, 0);
      this.noteExecuted('direct');
      return doFlap({ decision });
    }
    return noFlap({ decision });
  }

  reset(): void {
    this.inFlight = [];
    this.lastIntentMs = -Infinity;
    this.lastDecision = null;
  }

  status(): Record<string, unknown> {
    return {
      inFlight: this.inFlight.length,
      latencyMs: this.params.latencyMs,
      compensation: this.params.latencyCompensation,
    };
  }

  // ------------------------------------------------------------- in flight --

  /** Register that a flap intent has been dispatched to an actuator. */
  noteIntent(requestId: string, nowMs: number, expectedLatencyMs: number): void {
    this.lastIntentMs = nowMs;
    this.inFlight.push({ requestId, etaMs: nowMs + expectedLatencyMs, issuedMs: nowMs });
  }

  /** Register that a specific intent resolved -- executed, missed, or rejected. */
  noteExecuted(requestId: string): void {
    this.inFlight = this.inFlight.filter((f) => f.requestId !== requestId);
  }

  /** Retire the oldest intent, for input sources that carry no request id. */
  noteAnyExecuted(): void {
    this.inFlight.shift();
  }

  get pendingIntents(): number {
    return this.inFlight.length;
  }

  // ---------------------------------------------------------------- decide --

  /**
   * Pure decision function: game state in, intent out. Never touches the game,
   * never touches the stimulator. Call once per simulation tick.
   */
  decide(state: GameState, nowMs = state.timestamp): BotDecision {
    const p = this.params;
    const d = derive(state);

    this.expireInFlight(nowMs);

    const horizonMs = p.latencyCompensation ? Math.max(0, p.latencyMs) : 0;
    const horizonSec = horizonMs / 1000;

    // An impulse whose ETA has passed but which has not been observed as a flap
    // is *overdue*, not gone -- a stimulated arm is late far more often than it
    // is early. Clamping the offset to 0 makes the bot assume it lands this
    // tick. Dropping it instead makes the bot double-request, which on its own
    // turns a survivable 80 ms delay into a crash within four seconds.
    // `inFlightGraceMs` is what eventually writes an impulse off as missed.
    const pendingOffsets = p.accountForInFlight
      ? this.inFlight.map((f) => Math.max(0, (f.etaMs - nowMs) / 1000))
      : [];

    const predicted = predictWithFlaps(
      state.birdY, state.birdVelocityY, this.config, horizonSec, pendingOffsets,
    );

    const targetY = this.computeTarget(state, d.gapCenterY, d.distanceToNextPipe);
    const errorY = predicted.y - targetY;

    let flap = false;
    let reason = 'coast';
    let suppressed = false;

    // Floor guard: predicted to be dangerously low by the time an impulse could
    // land. Overrides the roof guard -- hitting the floor ends the run, brushing
    // the ceiling only costs velocity.
    const floorRisk = predicted.y > state.height - p.floorMargin;

    // Roof guard: the arc of a flap issued now would bury the bird in the
    // ceiling. Only the hard roof is tested. Whether the arc clips the lip of a
    // pipe is deliberately NOT tested: a static apex check there makes the bot
    // refuse to flap while falling at terminal velocity into a low gap, which
    // is the single worst failure mode this controller can have.
    const apexY = predicted.y - flapApexRise(this.config);
    const ceilingBlocked = apexY < p.ceilingMargin;

    if (floorRisk) {
      flap = true;
      reason = 'floor guard';
    } else if (errorY > p.deadband) {
      flap = true;
      reason = 'below target';
    }

    if (flap && ceilingBlocked && !floorRisk) {
      flap = false;
      reason = 'roof guard';
    }

    // There is deliberately no blanket "something is already in flight, stay
    // quiet" rule. Suppression is handled by the prediction itself: in-flight
    // impulses are folded into `predicted`, so if the bot still reads "too low"
    // after accounting for them, a second impulse really is warranted.
    if (flap && nowMs - this.lastIntentMs < p.minIntentIntervalMs) {
      flap = false;
      suppressed = true;
      reason = `${reason} (refractory)`;
    }

    const decision: BotDecision = {
      flap,
      targetY,
      errorY,
      timeToPipe: d.timeToPipeSec,
      reason,
      tick: state.tick,
      timestamp: nowMs,
      horizonMs,
      predictedY: predicted.y,
      predictedVy: predicted.vy,
      birdY: state.birdY,
      birdVelocityY: state.birdVelocityY,
      gapCenterY: d.gapCenterY,
      distanceToNextPipe: d.distanceToNextPipe,
      inFlight: this.inFlight.length,
      suppressed,
      predictedPath: p.collectPath ? this.forecastPath(state, pendingOffsets) : [],
    };
    this.lastDecision = decision;
    return decision;
  }

  /** Where the bird lands in `seconds` with no compensation and no in-flight. */
  uncompensatedPrediction(state: GameState, seconds: number): { y: number; vy: number } {
    return predict(state.birdY, state.birdVelocityY, this.config, seconds);
  }

  /**
   * Aim point: gap centre, biased down by a fraction of the flap arc, and
   * clamped so the bias can never push the target outside the gap itself.
   */
  private aimOffset(state: GameState): number {
    const rise = flapApexRise(this.config);
    if (!Number.isFinite(rise)) return 0;
    const halfGap = (state.nextPipeGapBottom - state.nextPipeGapTop) / 2;
    const room = Math.max(0, halfGap - state.collisionRadius - 6);
    return Math.max(-room, Math.min(room, rise * this.params.aimFraction));
  }

  /**
   * Where to steer.
   *
   * Starts at the gap centre, biased down by a fraction of the flap arc, then
   * pulled towards the gap *after* it -- and finally clamped back inside the
   * gap the bird is actually about to fly through.
   *
   * That clamp is what makes the anticipation safe. The bird can only descend
   * at its steady sink rate, so at low gravity a big drop between two gaps has
   * to be started a pipe early or it is physically unreachable. Aiming at the
   * far gap directly would pull the bird out of the near one; aiming at the
   * near gap's *low edge* pre-positions for the drop while still guaranteeing
   * passage. Adding this clamp took the low-gravity presets from unflyable to
   * comfortable.
   */
  private computeTarget(state: GameState, gapCenterY: number, distance: number): number {
    const p = this.params;
    let target = gapCenterY + this.aimOffset(state);

    if (distance > p.lookaheadDistance && state.pipes.length > 1) {
      const upcoming = state.pipes.find((pipe) => pipe.x > state.nextPipeX);
      if (upcoming) {
        const upcomingCenter = (upcoming.gapTop + upcoming.gapBottom) / 2;
        const blend =
          Math.min(1, (distance - p.lookaheadDistance) / Math.max(1, this.config.pipeSpacing)) *
          p.lookaheadWeight;
        target = target * (1 - blend) + upcomingCenter * blend;
      }
    }

    // Never aim outside the gap in front of us, however inviting the next one is.
    const pad = state.collisionRadius + 6;
    const lo = state.nextPipeGapTop + pad;
    const hi = state.nextPipeGapBottom - pad;
    return hi > lo ? Math.max(lo, Math.min(hi, target)) : (lo + hi) / 2;
  }

  private forecastPath(
    state: GameState,
    pendingOffsets: readonly number[],
  ): { t: number; y: number }[] {
    const out: { t: number; y: number }[] = [];
    for (let i = 0; i <= PATH_SAMPLES; i++) {
      const t = (i / PATH_SAMPLES) * PATH_HORIZON_SEC;
      const at = predictWithFlaps(
        state.birdY, state.birdVelocityY, this.config, t, pendingOffsets,
      );
      out.push({ t, y: at.y });
    }
    return out;
  }

  /**
   * Stop believing in impulses that are overdue past the grace period.
   *
   * Measured from the ETA, not from when the request was issued, so the same
   * grace means the same thing whether the actuator takes 50 ms or 200 ms.
   */
  private expireInFlight(nowMs: number): void {
    if (this.inFlight.length === 0) return;
    const grace = this.params.inFlightGraceMs;
    this.inFlight = this.inFlight.filter((f) => nowMs <= f.etaMs + grace);
  }
}
